import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { LicenseService } from "../../services/license/license.service";
import { LicenseV2Service } from "../../../subscriptions-plus/service/subscriptions-plus.service";
import { LicenseActions } from "./license.actions";
import { ToastService } from "../../services/toast/toast.service";
import { withLatestFrom, filter, switchMap, mergeMap, map, catchError, of } from "rxjs";
import { ILicense, ILicenseV2, IUser, ToastType } from "../../models";
import { selectLoggedInUser, selectUser } from "../user/user.selectors";
import { Store } from "@ngrx/store";
import { AppState } from "../root";

@Injectable()
export class LicenseEffects {
  private actions$ = inject(Actions);
  private licenseService = inject(LicenseService);
  private licenseV2Service = inject(LicenseV2Service);
  private toastService = inject(ToastService);
  private store$ = inject(Store<AppState>);

  fetchUserLicenses$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.fetchUsersLicenses),
    withLatestFrom(this.store$.select(selectUser)),
    filter(([_, user]) => !!user),
    switchMap(([action, user]) => {
      const currentUser = user as IUser;
      return this.licenseService.getLicenses({
        organisation_id: currentUser.organisation_id
      }).pipe(
        map(licenses => {
          return LicenseActions.fetchLicensesSuccess({ licenses })
        }),
        catchError(error => {
          return of(LicenseActions.fetchLicensesFailure({ error }))
        })
      );
    })
  )
  );

  loadSubscriptions$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.loadSubscriptions),
    withLatestFrom(this.store$.select(selectUser)),
    filter(([_, user]) => !!user),
    switchMap(([_action, user]) => {
      const currentUser = user as IUser;
      return this.licenseService.getSubscriptions(currentUser).pipe(
        map(subscriptions => {
          return LicenseActions.loadSubscriptionsSuccess({ subscriptionState: subscriptions })
        }),
        catchError(error => {
          return of(LicenseActions.loadSubscriptionsFailure({ error }))
        })
      );
    })
  )
  );

  updateSubscription$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.updateSubscription),
    switchMap(action => {
      return this.licenseService.updateLicense(action.license.id, action.name, action.notes).pipe(
        map(e => {
          this.toastService.showToast("Subscription updated successfully", ToastType.SUCCESS)
          return LicenseActions.updateSubscriptionSuccess({ license: { ...action.license, name: action.name, notes: action.notes } })
        },
          catchError(error => {
            this.toastService.showToast("Failed to update subscription", ToastType.ERROR)
            return of(LicenseActions.updateSubscriptionFailure({ license: action.license, error: error }))
          }))
      )
    }),
  ))

  revokeSubscription$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.revokeSubscription),
    switchMap(action => {
      return this.licenseService.revokeLicense(action.license).pipe(
        map(e => {
          this.toastService.showToast("Subscription revoked successfully", ToastType.SUCCESS)
          return LicenseActions.revokeSubscriptionSuccess({ licenses: e, })
        },
          catchError(error => {
            this.toastService.showToast("Failed to revoke subscription", ToastType.ERROR)
            return of(LicenseActions.revokeSubscriptionFailure({ license: action.license, error: error }))
          }))
      )
    }),
  ))

  distributeLicenses$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.distributeLicenses),
    switchMap(action => {
      return this.licenseService.distributeLicense(action.request).pipe(
        map(e => {
          this.toastService.showToast("Distributed licenses successfully", ToastType.SUCCESS)
          return LicenseActions.distributeLicensesSuccess({ license: e, })
        },
          catchError(error => {
            this.toastService.showToast("Failed to distribute licenses", ToastType.ERROR)
            return of(LicenseActions.distributeLicensesFailure({ error: error }))
          }))
      )
    }),
  ))

  requestLicenses$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.requestLicenses),
    withLatestFrom(this.store$.select(selectLoggedInUser)),
    switchMap(([action, user]) => {
      return this.licenseService.addLicense(action.request).pipe(
        map(e => {
          this.toastService.showToast("Requested licenses successfully", ToastType.SUCCESS)
          let license: ILicense = {
            ...e,
            is_distributed_license: e.organisation_id !== user.organisation_id
          }
          return LicenseActions.requestLicensesSuccess({ license: license, })
        },
          catchError(error => {
            this.toastService.showToast("Failed to request licenses", ToastType.ERROR)
            return of(LicenseActions.requestLicensesFailure({ error: error }))
          }))
      )
    }),
  ))

  fetchV2Licenses$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.fetchV2Licenses),
    switchMap(() => {
      return this.licenseService.getV2Licenses().pipe(
        map((licenses: ILicenseV2[]) => {
          return LicenseActions.fetchV2LicensesSuccess({ licenses });
        }),
        catchError(error => {
          return of(LicenseActions.fetchV2LicensesFailure({ error }));
        })
      );
    })
  ));

  fetchEntityLinks$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.fetchEntityLinks),
    switchMap(action => {
      return this.licenseV2Service.getLinksByEntities(action.entityIds).pipe(
        map(links => {
          return LicenseActions.fetchEntityLinksSuccess({ links });
        }),
        catchError(error => {
          return of(LicenseActions.fetchEntityLinksFailure({ error }));
        })
      );
    })
  ));

  fetchCameraLicenses$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.fetchCameraLicenses),
    switchMap(action => {
      return this.licenseService.getCameraLicenses(action.cameraIds).pipe(
        map(response => {
          return LicenseActions.fetchCameraLicensesSuccess({ cameraLicenses: response.cameraLicenses });
        }),
        catchError(error => {
          return of(LicenseActions.fetchCameraLicensesFailure({ error }));
        })
      );
    })
  ));

  fetchFamilyUsage$ = createEffect(() => this.actions$.pipe(
    ofType(LicenseActions.fetchFamilyUsage),
    mergeMap(({ family }) => this.licenseService.getFamilyUsage(family).pipe(
      map(usage => LicenseActions.fetchFamilyUsageSuccess({ family, usage })),
      catchError(error => of(LicenseActions.fetchFamilyUsageFailure({ family, error }))),
    ))
  ));

}
